import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Typography,
  Grid,
  Button,
  Box,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import { useUserContext } from "../../../context/UserContext";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import useStyles from "./DAF2TreasuryModeler.styles";
import CashflowTable from "./tables/CashflowTable.js";
import InvestmentsTable from "./tables/InvestmentsTable.js";
import CommercialPaperTable from "./tables/CommercialPaperTable";
import CreditFacilityTable from "./tables/CreditFacilityTable.js";
import STReceivablesOnBsTable from "./tables/STReceivablesOnBsTable.js";
import STLiabilitiesOnBsTable from "./tables/STLiabilitiesOnBsTable.js";
import TimelineTable from "./tables/TimelineTable.js";
import { creditFacilityResponse } from "./responseApi";
import { mainResponse } from "./responseApi.js";
import { downloadElementsAsPdf } from "../../common/Pdf/pdfDownloader.js";
import {
  getMappedBusinessEntityIDs,
  getSimpleFormattedDate,
} from "../../../utils/utils.js";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext.js";

const mapScenarioMnemonics = (scenarioMnemonic) => ({
  id: scenarioMnemonic?.mnemonic,
  name: scenarioMnemonic?.mnemonic,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const DAF2TreasuryModelerFiltersAndReports = () => {
  const [creditFacilityTableDatas, setCreditFacilityTableDatas] = useState([]);
  const [liquidityModelTableColumns, setLiquidityModelTableColumns] = useState(
    [],
  );
  const [liquidityModelTableDatas, setLiquidityModelTableDatas] = useState([]);
  const [investmentsTableDatas, setInvestmentsTableDatas] = useState([]);
  const [sTReceivablesOnBsTableDatas, setSTReceivablesOnBsTableDatas] =
    useState([]);
  const [sTLiabilitiesOnBsTableDatas, setSTLiabilitiesOnBsTableDatas] =
    useState([]);

  const [isWritableSfc, setIsWritableSfc] = useState(false);
  const { selectedUserDetailLoading, selectedUserDetail, isAdmin } =
    useUserContext();

  const [filterChangeDetected, setFilterChangeDetected] = useState(false);

  const { classes } = useStyles();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    localCurrency,
    setLocalCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    scenarioMnemonicsData,
    selectedScenarioMnemonics,
    setSelectedScenarioMnemonics,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
  } = useHedgeRatioContext();

  const { metricsHistoryUpsertAndSave, metricsHistoryUpsertAndSaveLoading } =
    useTreasuryKRISummaryContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency, writable } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
    setIsWritableSfc(writable);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const mappedScenarioMnemonics = [
    ...(scenarioMnemonicsData || []).map(mapScenarioMnemonics),
  ];

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  const handleSubmitBtn = () => {
    setFilterChangeDetected(false);
    if (
      selectedModelDatabase &&
      selectedBusinessEntityID &&
      selectedScenarioMnemonics
    ) {
      alert("Call Api to fetch data once its implemented!");
    } else {
      alert("Select the appropriate filters!");
      // setData([]);
    }
  };

  const handleSaveDAF2ModelerHistory = () => {
    if (
      parseInt(selectedFigureDivisor) === 1 &&
      parseInt(selectedDecimalsIn) === 0
    ) {
      metricsHistoryUpsertAndSave([
        {
          dimension: null,
          dimension2: null,
          measure: null,
          businessEntityID: selectedBusinessEntityID,
          value: null + "%",
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
      ]);
    } else {
      alert(
        "Please change the Figure Divisor selection to 'Raw' and Decimals selection to 'No Decimal' before saving.",
      );
    }
  };

  const reportRef = useRef(null);

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    const elementsToPrint = [reportRef.current];
    await downloadElementsAsPdf(
      elementsToPrint,
      `DAF2TreasuryModeler_${getSimpleFormattedDate()}`,
    );
    setIsExporting(false);
  };

  const [isExporting, setIsExporting] = useState(false);

  return (
    <>
      <Card
        style={{
          padding: "6px",
          marginBottom: "20px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "#6a6a6a",
            marginBottom: 2,
            fontWeight: "bold",
          }}
        >
          The below data shows DAF2 Template - Liquidity Risk (Modeler View) for
          the selected model database.
        </Typography>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id={"business-entity"}>Business Entity</InputLabel>
            <Autocomplete
              options={mappedBusinessEntityIDs}
              getOptionLabel={(option) => option?.name || ""}
              value={
                mappedBusinessEntityIDs.find(
                  (item) => item.id === selectedBusinessEntityID,
                ) || null
              }
              onChange={(_, newValue) => {
                setSelectedBusinessEntityID(newValue ? newValue.id : null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={selectedUserDetailLoading || hrFiltersDataLoading}
              ListboxProps={{
                style: {
                  width: "max-content",
                  minWidth: "100%",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder="Select Business Entity"
                  size="small"
                />
              )}
              sx={{ width: 180 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id={"model-database"}>Model Database</InputLabel>
            <Autocomplete
              options={mappedModelDatabases}
              getOptionLabel={(option) => option?.name || ""}
              value={
                mappedModelDatabases.find(
                  (item) => item.id === selectedModelDatabase,
                ) || null
              }
              onChange={(_, newValue) => {
                setSelectedModelDatabase(newValue ? newValue.id : null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={selectedUserDetailLoading || hrFiltersDataLoading}
              ListboxProps={{
                style: {
                  width: "max-content",
                  minWidth: "100%",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder="Select Model Database"
                  size="small"
                />
              )}
              sx={{ width: 300 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id={"scenario-mnemonics"}>
              Scenario Mnemonics
            </InputLabel>
            <Autocomplete
              options={mappedScenarioMnemonics}
              getOptionLabel={(option) => option?.name || ""}
              value={
                mappedScenarioMnemonics.find(
                  (item) => item.id === selectedScenarioMnemonics,
                ) || null
              }
              onChange={(_, newValue) => {
                setSelectedScenarioMnemonics(newValue ? newValue.id : null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={selectedUserDetailLoading || hrFiltersDataLoading}
              ListboxProps={{
                style: {
                  width: "max-content",
                  minWidth: "100%",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder="Select Scenario Mnemonics"
                  size="small"
                />
              )}
              sx={{ width: 300 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmitBtn}
              sx={{
                fontSize: "0.8rem",
                boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                height: "38px",
                mt: 3,
              }}
              disabled={
                selectedUserDetailLoading ||
                hrFiltersDataLoading ||
                !selectedBusinessEntityID ||
                !selectedModelDatabase ||
                !selectedScenarioMnemonics
              }
            >
              Submit
            </Button>
          </Grid>
          {/* <Grid
            item
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <div className={classes.DAF2ModelerExportBtn}>
              <DAF2TreasuryModelerExport
                columns={[]}
                datas={[]}
                disabled={true}
              />
            </div>
          </Grid> */}
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<PictureAsPdf />}
              onClick={handleDownloadPDF}
              sx={{
                fontSize: "0.8rem",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                height: "40px",
              }}
              disabled={
                selectedUserDetailLoading ||
                !selectedBusinessEntityID ||
                !selectedModelDatabase ||
                !selectedScenarioMnemonics
              }
            >
              Export to PDF
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InlineDropdown
              label="Show Figures In"
              labelId="show-figures-in"
              options={mappedFigureDivisors}
              selectedValue={selectedFigureDivisor}
              handleChange={setSelectedFigureDivisor}
              placeholder={"Show Figures In"}
              dropdownWidth={150}
              isLoading={selectedUserDetailLoading || hrFiltersDataLoading}
            />
          </Grid>
          {localCurrency && (
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id="currency-type">Currency</InputLabel>
              <Typography
                sx={{
                  color: "#6a6a6a",
                  fontWeight: "bold",
                  marginTop: 1,
                }}
              >
                {localCurrency}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <InlineDropdown
              label="Show Decimal In"
              labelId="show-decimal-in"
              options={mappedDecimalsIn}
              selectedValue={selectedDecimalsIn}
              handleChange={setSelectedDecimalsIn}
              placeholder={"Show Decimals In"}
              dropdownWidth={150}
              isLoading={selectedUserDetailLoading || hrFiltersDataLoading}
            />
          </Grid>
        </Grid>
      </Card>

      <>
        {/* Check for data loading condition */}
        {false ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              There was an error in fetching the data. Please try again later.
            </Typography>
          </Item>
        ) : false ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              No data available for the selected filters.
            </Typography>
          </Item>
        ) : (
          <div ref={reportRef}>
            <Card
              style={{
                padding: "6px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <CashflowTable
                mainResponse={mainResponse}
                creditFacilityTableDatas={creditFacilityTableDatas}
                setLiquidityModelTableColumns={setLiquidityModelTableColumns}
                setLiquidityModelTableDatas={setLiquidityModelTableDatas}
              />
            </Card>
            <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <InvestmentsTable
                    setInvestmentsTableDatas={setInvestmentsTableDatas}
                  />
                  <div style={{ marginTop: "2px" }}>
                    <CommercialPaperTable />               
                  </div>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <CreditFacilityTable
                    setCreditFacilityTableDatas={setCreditFacilityTableDatas}
                    creditFacilityResponse={creditFacilityResponse}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <STReceivablesOnBsTable
                    setSTReceivablesOnBsTableDatas={
                      setSTReceivablesOnBsTableDatas
                    }
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Card style={{ height: "90%" }}>
                  <STLiabilitiesOnBsTable
                    setSTLiabilitiesOnBsTableDatas={
                      setSTLiabilitiesOnBsTableDatas
                    }
                  />
                </Card>
              </Grid>
            </Grid>
            <Card
              style={{
                padding: "6px",
                marginBottom: "20px",
              }}
            >
              <TimelineTable
                liquidityModelTableColumns={liquidityModelTableColumns}
                liquidityModelTableDatas={liquidityModelTableDatas}
                investmentsTableDatas={investmentsTableDatas}
                sTReceivablesOnBsTableDatas={sTReceivablesOnBsTableDatas}
                sTLiabilitiesOnBsTableDatas={sTLiabilitiesOnBsTableDatas}
              />
            </Card>
            {(isWritableSfc || isAdmin) && (
              <Grid
                container
                spacing={2}
                sx={{ marginBottom: 2, width: "100%" }}
              >
                <Grid
                  size={{ xs: 12, md: 12, lg: 12 }}
                  sx={{ display: isExporting ? "none" : "block" }} // This will be igored while exporting to pdf
                >
                  <Box
                    sx={{
                      marginBottom: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className={classes.SaveBtn}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDAF2ModelerHistory}
                        sx={{
                          fontSize: "0.8rem",
                          boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                          height: "40px",
                        }}
                        disabled={
                          metricsHistoryUpsertAndSaveLoading ||
                          filterChangeDetected ||
                          true
                        }
                      >
                        Save Data
                      </Button>
                    </div>
                  </Box>
                </Grid>
              </Grid>
            )}
          </div>
        )}
      </>
    </>
  );
};

export default DAF2TreasuryModelerFiltersAndReports;
